/**
 * CalendarGridView: 月表示カレンダーグリッドによる日程選択の共通UIコンポーネント。
 * design-mockups/survey-popup.html, pre-booking-form.html, multi-cv-appointment-list.html準拠。
 *
 * 空き枠（AppointmentSlot）の配列から月表示グリッドを構築し、日付選択→その日の時間帯選択という
 * 2段階のUIをカレンダーグリッド内に統合する。PreBookingForm・SurveyPopup（アポ打診）・
 * MultiCvAppointmentList・希望日程入力（アポイントリクエスト）の各箇所で共通利用する。
 */

import { createElement, setText } from "../core/shadowDomUtil";
import type { AppointmentSlot } from "../types";
import type { CalendarModalHandle } from "./calendarModalUi";
import { formatSlotLabel, formatDateHeadingLabel, formatTimeLabel } from "./datetime";

const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

export interface CalendarGridCallbacks {
  /** 日付（YYYY-MM-DD）とその日のスロット一覧を選択した際に呼ばれる。 */
  onSlotSelected?: (slot: AppointmentSlot) => void;
}

export interface CalendarGridHandle {
  /** 現在選択中のスロットを取得する。 */
  getSelectedSlot: () => AppointmentSlot | null;
  /** カレンダーのルート要素。 */
  element: HTMLElement;
}

/**
 * 月表示カレンダーグリッドを構築する（`spica-calendar-*`クラス、design-mockups準拠）。
 * `slots`が空の場合でも、全日程を選択不可（`disabled`）としたグリッドを表示する
 * （BR-U6-1: 未ログイン時モザイク背景としても利用するため、空データでも描画できる必要がある）。
 */
export function renderCalendarGrid(
  slots: AppointmentSlot[],
  callbacks: CalendarGridCallbacks = {},
  options?: { initialDateSelected?: boolean }
): CalendarGridHandle {
  const slotsByDate = groupSlotsByDate(slots);
  const dateKeys = Array.from(slotsByDate.keys()).sort();

  const root = createElement("div", { className: "spica-calendar-view" });
  let selectedSlot: AppointmentSlot | null = null;
  // design-mockups準拠: initialDateSelected=false（モーダル展開時）は日付未選択で「日付を選択してください」表示
  let selectedDateKey: string | null = (options?.initialDateSelected === false) ? null : (dateKeys[0] ?? null);

  // 表示対象の月（データがあれば最初の空き枠の月、なければ現在月）。
  // 日付キー（YYYY-MM-DD）から文字列操作で年・月を導出する（`new Date(iso)` 不使用、TZ非依存）。
  let viewYear: number;
  let viewMonth: number; // 0-indexed
  if (dateKeys.length > 0) {
    const [y, m] = dateKeys[0].split("-");
    viewYear = Number(y);
    viewMonth = Number(m) - 1;
  } else {
    const now = new Date();
    viewYear = now.getFullYear();
    viewMonth = now.getMonth();
  }

  const header = createElement("div", { className: "spica-calendar-header" });
  const prevButton = createElement("button", { className: "spica-calendar-nav-btn", text: "<" });
  const monthLabel = createElement("span", { className: "spica-calendar-month-label" });
  const nextButton = createElement("button", { className: "spica-calendar-nav-btn", text: ">" });
  header.appendChild(prevButton);
  header.appendChild(monthLabel);
  header.appendChild(nextButton);
  root.appendChild(header);

  const grid = createElement("div", { className: "spica-calendar-grid" });
  root.appendChild(grid);

  const daySlotsSection = createElement("div", { className: "spica-calendar-day-slots" });
  const daySlotsLabel = createElement("p", { className: "spica-calendar-day-slots-label" });
  const slotList = createElement("ul", { className: "spica-calendar-slot-list" });
  daySlotsSection.appendChild(daySlotsLabel);
  daySlotsSection.appendChild(slotList);
  root.appendChild(daySlotsSection);

  function renderMonth(): void {
    setText(monthLabel, `${viewYear}年${viewMonth + 1}月`);
    grid.textContent = "";

    for (const w of WEEKDAY_LABELS) {
      grid.appendChild(createElement("div", { className: "spica-calendar-weekday", text: w }));
    }

    const firstDay = new Date(viewYear, viewMonth, 1);
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const startWeekday = firstDay.getDay();

    for (let i = 0; i < startWeekday; i++) {
      grid.appendChild(createElement("span"));
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(viewYear, viewMonth, day);
      const hasSlot = slotsByDate.has(dateKey);
      const dayButton = createElement("button", {
        className: hasSlot ? "spica-calendar-day has-slot" : "spica-calendar-day disabled",
        text: String(day),
      }) as HTMLButtonElement;
      dayButton.disabled = !hasSlot;
      if (dateKey === selectedDateKey) {
        dayButton.classList.add("selected");
      }
      if (hasSlot) {
        dayButton.addEventListener("click", () => {
          selectedDateKey = dateKey;
          selectedSlot = null;
          renderMonth();
          renderDaySlots();
        });
      }
      grid.appendChild(dayButton);
    }
  }

  function renderDaySlots(): void {
    slotList.textContent = "";
    if (!selectedDateKey || !slotsByDate.has(selectedDateKey)) {
      // design-mockups準拠: 日付未選択時は「日付を選択してください」を表示
      if (!selectedDateKey) {
        daySlotsLabel.style.display = "";
        slotList.style.display = "none";
        setText(daySlotsLabel, "日付を選択してください");
      } else {
        daySlotsLabel.style.display = "none";
        slotList.style.display = "none";
      }
      return;
    }
    daySlotsLabel.style.display = "";
    slotList.style.display = "";

    const dateLabel = formatDateHeadingLabel(selectedDateKey);
    setText(daySlotsLabel, `${dateLabel}の空き時間`);

    const daySlots = slotsByDate.get(selectedDateKey)!;
    for (const slot of daySlots) {
      const li = createElement("li");
      const timeLabel = formatTimeLabel(slot.startAt);
      const slotButton = createElement("button", { className: "spica-calendar-slot-btn", text: timeLabel });
      if (selectedSlot && selectedSlot.startAt === slot.startAt) {
        slotButton.classList.add("selected");
      }
      slotButton.addEventListener("click", () => {
        selectedSlot = slot;
        for (const btn of slotList.querySelectorAll(".spica-calendar-slot-btn")) {
          btn.classList.remove("selected");
        }
        slotButton.classList.add("selected");
        callbacks.onSlotSelected?.(slot);
      });
      li.appendChild(slotButton);
      slotList.appendChild(li);
    }
  }

  prevButton.addEventListener("click", () => {
    viewMonth -= 1;
    if (viewMonth < 0) {
      viewMonth = 11;
      viewYear -= 1;
    }
    renderMonth();
  });
  nextButton.addEventListener("click", () => {
    viewMonth += 1;
    if (viewMonth > 11) {
      viewMonth = 0;
      viewYear += 1;
    }
    renderMonth();
  });

  renderMonth();
  renderDaySlots();

  return {
    element: root,
    getSelectedSlot: () => selectedSlot,
  };
}

/** 初期表示するスロット件数（design-mockups準拠: 3件程度+もっと見るボタン）。 */
const INITIAL_VISIBLE_SLOT_COUNT = 3;

export interface SlotPickerHandle {
  /** 現在選択中のスロットを取得する。 */
  getSelectedSlot: () => AppointmentSlot | null;
  /** ルート要素。 */
  element: HTMLElement;
}

export interface SlotPickerOptions extends CalendarGridCallbacks {
  /**
   * 指定した場合、「別の日時から選ぶ」押下時にカード内インライン展開ではなく、
   * 共通の中央カレンダーモーダル（`calendarModalUi.ts`）へ展開する
   * （2026-08-19デザイン更新、design-mockups準拠）。未指定時は従来のインライン展開を維持する。
   */
  calendarModal?: CalendarModalHandle;
  /** カレンダーモーダルのヘッダーに表示する企業名。 */
  companyName?: string;
  /** カレンダーモーダルのヘッダーに表示するコンテンツ名。 */
  contentName?: string;
}

/**
 * SlotPicker: 初期表示は先頭数件のスロットボタン＋「別の日時から選ぶ」ボタン、
 * 押下でカレンダーグリッド（CalendarGridView）に展開する2段階UI。
 * design-mockups/*.html（2026-08-06最終版）準拠: 絵文字アイコンは廃止しテキストのみとし、
 * 展開先は`calendarModal`指定時は共通の中央カレンダーモーダル、未指定時は従来のカード内展開。
 */
export function renderSlotPicker(
  slots: AppointmentSlot[],
  options: SlotPickerOptions = {}
): SlotPickerHandle {
  const { calendarModal, companyName, contentName, ...callbacks } = options;
  const root = createElement("div");
  let selectedSlot: AppointmentSlot | null = null;

  const initialSlots = slots.slice(0, INITIAL_VISIBLE_SLOT_COUNT);

  const slotList = createElement("ul", { className: "spica-slot-list" });
  const slotButtons: HTMLButtonElement[] = [];

  function selectSlot(slot: AppointmentSlot, button: HTMLButtonElement, allButtons: HTMLButtonElement[]): void {
    selectedSlot = slot;
    for (const b of allButtons) b.classList.remove("selected");
    button.classList.add("selected");
    // モック準拠: 初期スロットが選択されたら「別の日時から選ぶ」を元のテキスト・未選択状態に戻す
    moreButton.textContent = "別の日時から選ぶ";
    moreButton.classList.remove("selected");
    callbacks.onSlotSelected?.(slot);
  }

  for (const slot of initialSlots) {
    const li = createElement("li");
    const label = formatSlotButtonLabel(slot);
    const button = createElement("button", { className: "spica-slot-btn", text: label }) as HTMLButtonElement;
    button.addEventListener("click", () => selectSlot(slot, button, slotButtons));
    slotButtons.push(button);
    li.appendChild(button);
    slotList.appendChild(li);
  }

  const moreLi = createElement("li");
  const moreButton = createElement("button", {
    className: "spica-slot-btn spica-slot-more-btn",
    text: "別の日時から選ぶ",
  }) as HTMLButtonElement;
  moreLi.appendChild(moreButton);
  slotList.appendChild(moreLi);
  root.appendChild(slotList);

  let calendarHandle: CalendarGridHandle | null = null;
  let expandedView: HTMLElement | null = null;
  let isExpanded = false;

  function buildExpandedView(onBack: () => void, options?: { hideBackButton?: boolean }): HTMLElement {
    const view = createElement("div", { className: "spica-slot-expanded" });
    if (!options?.hideBackButton) {
      // インライン展開時のみ「← 戻る」ボタンを表示する（モーダル展開時はモーダルの×ボタンで閉じる）
      const backButton = createElement("button", { className: "spica-slot-back-btn", text: "← 戻る" });
      backButton.addEventListener("click", onBack);
      view.appendChild(backButton);
    }

    calendarHandle = renderCalendarGrid(slots, {
      onSlotSelected: (slot) => {
        selectedSlot = slot;
        // モック準拠: モーダルからスロット選択時、「別の日時から選ぶ」ボタンを選択した日時テキストに更新し、
        // 初期スロットボタンの選択状態をクリアする
        if (calendarModal) {
          const slotLabel = formatSlotButtonLabel(slot);
          moreButton.textContent = slotLabel;
          moreButton.classList.add("selected");
          for (const b of slotButtons) b.classList.remove("selected");
        }
        callbacks.onSlotSelected?.(slot);
        // モック準拠: カレンダーモーダル使用時は時間帯選択でモーダルを閉じてカード本体に反映
        if (calendarModal) {
          calendarModal.close();
        }
      },
    }, { initialDateSelected: !options?.hideBackButton });
    calendarHandle.element.classList.add("spica-calendar-view", "is-open");
    view.appendChild(calendarHandle.element);
    return view;
  }

  function collapseCalendar(): void {
    root.classList.remove("spica-slot-picker-expanded");
    slotList.style.display = "";
    isExpanded = false;
    if (expandedView) {
      expandedView.remove();
      expandedView = null;
    }
    calendarHandle = null;
  }

  function expandCalendarInline(): void {
    root.classList.add("spica-slot-picker-expanded");
    slotList.style.display = "none";
    isExpanded = true;
    expandedView = buildExpandedView(collapseCalendar);
    root.appendChild(expandedView);
  }

  function resetExpandedState(): void {
    slotList.style.display = "";
    isExpanded = false;
    expandedView = null;
    calendarHandle = null;
  }

  function expandCalendarInModal(modal: CalendarModalHandle): void {
    isExpanded = true;
    expandedView = buildExpandedView(() => modal.close(), { hideBackButton: true });
    modal.openWithElement(expandedView, resetExpandedState, { company: companyName, content: contentName });
  }

  moreButton.addEventListener("click", () => {
    if (isExpanded) {
      if (calendarModal) {
        calendarModal.close();
      } else {
        collapseCalendar();
      }
      return;
    }
    if (calendarModal) {
      expandCalendarInModal(calendarModal);
    } else {
      expandCalendarInline();
    }
  });

  return {
    element: root,
    getSelectedSlot: () => (calendarHandle ? calendarHandle.getSelectedSlot() : selectedSlot),
  };
}

/** スロットボタンの表示ラベル（例: "7/16(木) 10:00〜"）を組み立てる。確定ボタンの動的文言にも使用する。 */
export function formatSlotButtonLabel(slot: AppointmentSlot): string {
  // JSTローカル文字列を文字列操作で整形する（`new Date(iso)` 不使用、TZ非依存。datetime-contract.md §4）。
  return formatSlotLabel(slot.startAt);
}

/** スロット配列を日付文字列（YYYY-MM-DD）でグループ化する。 */
function groupSlotsByDate(slots: AppointmentSlot[]): Map<string, AppointmentSlot[]> {
  const map = new Map<string, AppointmentSlot[]>();
  for (const slot of slots) {
    // startAt は JST ローカル文字列（`yyyy-MM-ddTHH:mm:ss`）のため、substring(0,10) は
    // そのまま JST の日付（YYYY-MM-DD）を返し、日付キーが正しくJST日付になる（frontend-display-spec.md §4）。
    const dateKey = slot.startAt.substring(0, 10);
    if (!map.has(dateKey)) {
      map.set(dateKey, []);
    }
    map.get(dateKey)!.push(slot);
  }
  return map;
}

function formatDateKey(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}
